import { createContext, useContext, useLayoutEffect, useMemo, useState } from "react";

export type ThemeMode = "dark" | "light";

interface ThemeValue {
  readonly mode: ThemeMode;
  readonly toggle: () => void;
}

const ThemeContext = createContext<ThemeValue | null>(null);

const initialTheme = (): ThemeMode => {
  const stored = globalThis.localStorage?.getItem("vakilat-theme");
  if (stored === "dark" || stored === "light") {
    return stored;
  }
  return "light";
};

export const ThemeProvider = ({
  children,
}: {
  readonly children: React.ReactNode;
}): React.JSX.Element => {
  const [mode, setMode] = useState<ThemeMode>(initialTheme);

  useLayoutEffect(() => {
    document.documentElement.dataset["theme"] = mode;
    document.documentElement.style.colorScheme = mode;
    globalThis.localStorage?.setItem("vakilat-theme", mode);
  }, [mode]);

  const value = useMemo<ThemeValue>(
    () => ({
      mode,
      toggle: () => {
        setMode((current) => (current === "dark" ? "light" : "dark"));
      },
    }),
    [mode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};

export const useTheme = (): ThemeValue => {
  const value = useContext(ThemeContext);
  if (value === null) {
    throw new Error("useTheme must be used within ThemeProvider");
  }
  return value;
};
